import {
  GlowIdTypes,
  NftCollection,
  NftInfo,
  Solana,
  SolanaTransaction,
  TokenProgram,
} from "@glow/shared";
import TransactionZapIcon from "../../icons/activity/Transaction.svg";
import classNames from "classnames";
import React from "react";
import { CoinIcon } from "../coins/CoinIcon";
import { SolanaCoinIcon } from "../coins/SolanaCoinIcon";
import { GlowIdAvatar } from "../glow-id/GlowIdAvatar";
import { MarketplaceIcon } from "../nfts/MarketplaceIcon";
import { NftStack } from "../nfts/NftStack";
import { SquareNftImage } from "../nfts/SquareNftImage";

export type TransactionIconProps =
  | { type: "custom"; node: React.ReactNode }
  | { type: "solana" }
  | {
      type: "nft";
      nft: NftInfo;
      marketplace: Solana.Marketplace | null | undefined;
    }
  | { type: "image"; image: string }
  | { type: "nfts"; nfts: NftInfo[] }
  | { type: "nft_collection"; collection: NftCollection.BasicInfo }
  | { type: "nft_marketplace"; marketplace: Solana.Marketplace }
  | { type: "coin"; coin: TokenProgram.CoinInfo }
  | {
      type: "glow_id";
      info: Pick<GlowIdTypes.BasicInfo, "image" | "name" | "handle">;
    }
  | {
      type: "swap";
      coin_sent: SolanaTransaction.SwappedCoin;
      coin_received: SolanaTransaction.SwappedCoin;
    }
  | {
      type: "tokens";
      token_a: TokenProgram.CoinInfo | NftInfo;
      token_b: TokenProgram.CoinInfo | NftInfo;
    }
  | { type: "default" };

export const TransactionIcon = ({ icon }: { icon: TransactionIconProps }) => {
  if (icon.type === "solana") {
    return <SolanaCoinIcon />;
  }

  if (icon.type === "coin") {
    return <CoinIcon coin={icon.coin} />;
  }

  if (icon.type === "image") {
    return (
      <SquareNftImage
        imageUrl={icon.image}
        name={"Transaction"}
        size={40}
        rounded
      />
    );
  }

  if (icon.type === "nft") {
    return <NftIcon nft={icon.nft} marketplace={icon.marketplace || null} />;
  }

  if (icon.type === "nfts") {
    return <NftStack nfts={icon.nfts} size={40} />;
  }

  if (icon.type === "nft_collection") {
    return (
      <SquareNftImage
        imageUrl={icon.collection.image_url}
        name={icon.collection.name}
        size={40}
        rounded
      />
    );
  }

  if (
    icon.type === "nft_marketplace" &&
    // We do this check since the server could have a new marketplace
    Solana.Marketplaces.includes(icon.marketplace)
  ) {
    return <MarketplaceIcon marketplace={icon.marketplace} size={40} />;
  }

  if (icon.type === "glow_id" && icon.info) {
    return <GlowIdAvatar info={icon.info} size={40} />;
  }

  if (icon.type === "swap") {
    return (
      <div className="swap-icon">
        <div className="sent">
          {icon.coin_sent.type === "sol" ? (
            <SolanaCoinIcon className="swap-icon-inner" size={24} />
          ) : (
            <CoinIcon
              coin={icon.coin_sent.coin}
              className="swap-icon-inner"
              size={24}
            />
          )}
        </div>
        <div className="received">
          {icon.coin_received.type === "sol" ? (
            <SolanaCoinIcon className="swap-icon-inner" size={24} />
          ) : (
            <CoinIcon
              coin={icon.coin_received.coin}
              className="swap-icon-inner"
              size={24}
            />
          )}
        </div>

        <style jsx>{`
          .swap-icon {
            display: block;
            width: 2.5rem;
            height: 2.5rem;
            position: relative;

            .sent,
            .received {
              position: absolute;
            }

            .received {
              right: 0;
              bottom: 0;
              width: calc(24px + 4px);
              height: calc(24px + 4px);
              border: 2px solid var(--primary-bg-color);
              border-radius: 50%;
              overflow: hidden;
            }

            :global(.swap-icon-inner) {
              width: 100%;
              height: 100%;
            }
          }
        `}</style>
      </div>
    );
  }

  if (icon.type === "tokens") {
    return (
      <div className="tokens-icon">
        <div className="token-a">
          {icon.token_a.type === "coin" ? (
            <CoinIcon
              coin={icon.token_a}
              className="tokens-icon-inner"
              size={24}
            />
          ) : (
            <NftIcon
              nft={icon.token_a}
              className="tokens-icon-inner"
              marketplace={null}
              size={24}
            />
          )}
        </div>
        <div className="token-b">
          {icon.token_b.type === "coin" ? (
            <CoinIcon
              coin={icon.token_b}
              className="tokens-icon-inner"
              size={24}
            />
          ) : (
            <NftIcon
              nft={icon.token_b}
              className="tokens-icon-inner"
              marketplace={null}
              size={24}
            />
          )}
        </div>

        <style jsx>{`
          .tokens-icon {
            display: block;
            width: 2.5rem;
            height: 2.5rem;
            position: relative;

            .token-a,
            .token-b {
              position: absolute;
              width: 1.5rem;
              height: 1.5rem;
            }

            .token-b {
              right: 0;
              bottom: 0;
              width: calc(1.5rem + 4px);
              height: calc(1.5rem + 4px);
              overflow: hidden;

              :global(.tokens-icon-inner) {
                border: 2px solid var(--primary-bg-color);
                width: calc(1.5rem + 4px) !important;
                height: calc(1.5rem + 4px) !important;
              }
            }

            :global(.tokens-icon-inner) {
              width: 100%;
              height: 100%;
            }
          }
        `}</style>
      </div>
    );
  }

  if (icon.type === "custom") {
    return (
      <IconWrapper shape={"circle"}>
        <div className="custom-icon">{icon.node}</div>

        <style jsx>{`
          .custom-icon :global(svg) {
            display: block;
            width: 2.5rem;
            height: 2.5rem;
          }
        `}</style>
      </IconWrapper>
    );
  }

  return <DefaultIcon />;
};

const NftIcon = ({
  nft,
  className,
  marketplace,
  size = 40,
}: {
  nft: NftInfo;
  className?: string;
  marketplace: Solana.Marketplace | null;
  size?: number;
}) => {
  const image = nft.image;
  if (image) {
    return (
      <SquareNftImage
        className={className}
        imageUrl={image}
        marketplace={marketplace}
        name={nft.name ?? "Unknown NFT"}
        size={size}
        rounded
      />
    );
  }
  return <DefaultIcon />;
};

export const DefaultIcon = () => {
  return (
    <IconWrapper shape="circle">
      <div className="default-icon flex-center-center">
        <TransactionZapIcon />
      </div>

      <style jsx>{`
        .default-icon {
          color: var(--quaternary-bg-color);

          :global(svg) {
            width: 1.25rem;
            height: 1.25rem;
          }
        }
      `}</style>
    </IconWrapper>
  );
};

const IconWrapper = ({
  shape,
  children,
  className,
}: {
  shape: "square" | "circle";
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={classNames(
        "transaction-icon flex-center-center",
        shape,
        className,
      )}
    >
      {children}

      <style jsx>{`
        .transaction-icon {
          background-color: var(--secondary-bg-color);
          width: 2.5rem;
          height: 2.5rem;
          overflow: hidden;

          &.circle {
            border-radius: 50%;
          }

          &.square {
            border-radius: var(--border-radius);
          }
        }
      `}</style>
    </div>
  );
};
